import { getIntersectionRoot } from '@theme/scroll-container';
import * as maplibregl from './maplibre-gl.mjs';

const MAP_SOURCE = 'mission-city-nodes';
const CLUSTER_LAYER = 'mission-city-clusters';
const CLUSTER_COUNT_LAYER = 'mission-city-cluster-count';
const POINT_LAYER = 'mission-city-points';
const MAP_TIMEOUT_MS = 15000;

class MissionCityNodes extends HTMLElement {
  map = null;
  mapLoaded = false;
  mapLoading = false;
  selectedIndex = 0;
  selectedFeatureId = null;
  intersectionObserver = null;
  resizeObserver = null;
  loadTimer = null;
  abortController = null;

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.mapElement = this.querySelector('[data-city-map]');
    this.statusElement = this.querySelector('[data-map-status]');
    this.statusMessage = this.querySelector('[data-map-status-message]');
    this.cardsElement = this.querySelector('[data-city-cards]');
    this.listPanel = this.querySelector('[data-city-list-panel]');
    this.listToggle = this.querySelector('[data-city-list-toggle]');
    this.listButtons = [...this.querySelectorAll('[data-city-select]')];
    this.cards = [...this.querySelectorAll('[data-city-card]')];
    this.nodes = this.listButtons
      .map((button) => ({
        id: button.dataset.nodeId,
        longitude: Number(button.dataset.longitude),
        latitude: Number(button.dataset.latitude),
        button,
      }))
      .filter(
        (node) =>
          node.id &&
          Number.isFinite(node.longitude) &&
          Number.isFinite(node.latitude) &&
          node.longitude >= -180 &&
          node.longitude <= 180 &&
          node.latitude >= -85.051129 &&
          node.latitude <= 85.051129
      );

    const minZoomSetting = Number(this.dataset.minZoom);
    const maxZoomSetting = Number(this.dataset.maxZoom);
    this.minZoom = Math.min(minZoomSetting, maxZoomSetting);
    this.maxZoom = Math.max(minZoomSetting, maxZoomSetting);
    this.initialZoom = this.clampZoom(Number(this.dataset.initialZoom));
    this.selectedZoom = this.clampZoom(Number(this.dataset.selectedZoom));
    const initialLongitude = Number(this.dataset.initialLongitude);
    const initialLatitude = Number(this.dataset.initialLatitude);
    this.initialLongitude = Number.isFinite(initialLongitude) && initialLongitude >= -180 && initialLongitude <= 180 ? initialLongitude : 0;
    this.initialLatitude = Number.isFinite(initialLatitude) && initialLatitude >= -85.051129 && initialLatitude <= 85.051129 ? initialLatitude : 20;

    this.addEventListener('click', this.handleClick, { signal });
    let swipeStart = null;
    this.cardsElement?.addEventListener('touchstart', (event) => {
      swipeStart = event.touches.length === 1 && this.nodes.length > 1 ? event.touches[0] : null;
    }, { passive: true, signal });
    this.cardsElement?.addEventListener('touchmove', (event) => {
      if (!swipeStart) return;
      if (event.touches.length !== 1) {
        swipeStart = null;
        return;
      }
      const dx = Math.abs(event.touches[0].clientX - swipeStart.clientX);
      const dy = Math.abs(event.touches[0].clientY - swipeStart.clientY);
      if (dy > 10 && dy >= dx) swipeStart = null;
      else if (dx > 10 && dx > dy * 1.25) event.preventDefault();
    }, { passive: false, signal });
    this.cardsElement?.addEventListener('touchend', (event) => {
      const start = swipeStart;
      swipeStart = null;
      if (!start || event.touches.length) return;
      const end = [...event.changedTouches].find((touch) => touch.identifier === start.identifier);
      if (!end) return;
      const dx = end.clientX - start.clientX;
      const dy = end.clientY - start.clientY;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
      event.preventDefault();
      this.selectNode(this.selectedIndex + (dx < 0 ? 1 : -1), true);
    }, { passive: false, signal });
    this.cardsElement?.addEventListener('touchcancel', () => { swipeStart = null; }, { passive: true, signal });
    this.listPanel?.addEventListener('toggle', () => {
      this.listToggle?.setAttribute('aria-expanded', String(this.listPanel.open));
    }, { signal });
    if (!this.nodes.length || !this.mapElement) return;

    this.selectNode(0, false);
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.intersectionObserver?.disconnect();
        this.initializeMap();
      },
      { root: getIntersectionRoot(), rootMargin: '300px 0px' }
    );
    this.intersectionObserver.observe(this.mapElement);

    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.mapElement);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    clearTimeout(this.loadTimer);
    this.map?.remove();
    this.map = null;
    this.mapLoaded = false;
    this.mapLoading = false;
  }

  clampZoom(value) {
    const fallback = Number.isFinite(value) ? value : this.minZoom;
    return Math.min(this.maxZoom, Math.max(this.minZoom, fallback));
  }

  initializeMap() {
    if (this.map || this.mapLoading || !this.mapElement) return;
    if (!this.dataset.apiKey) {
      this.showMapFailure(this.dataset.keyMessage);
      return;
    }
    this.hideMapFailure();
    this.mapLoading = true;
    const dragEnabled = this.dataset.dragEnabled === 'true';
    const zoomEnabled = this.dataset.zoomEnabled === 'true';
    const styleUrl = this.dataset.styleUrl || `https://api.maptiler.com/maps/basic-v2/style.json?key=${encodeURIComponent(this.dataset.apiKey)}`;

    try {
      this.map = new maplibregl.Map({
        container: this.mapElement,
        style: styleUrl,
        center: [this.initialLongitude, this.initialLatitude],
        zoom: this.initialZoom,
        minZoom: this.minZoom,
        maxZoom: this.maxZoom,
        maxBounds: [[-179.999, -80], [179.999, 80]],
        dragPan: dragEnabled,
        scrollZoom: zoomEnabled,
        doubleClickZoom: zoomEnabled,
        boxZoom: zoomEnabled,
        touchZoomRotate: zoomEnabled,
        keyboard: dragEnabled || zoomEnabled,
        cooperativeGestures: true,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        renderWorldCopies: false,
        attributionControl: true,
      });

      if (zoomEnabled) this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

      this.loadTimer = setTimeout(() => {
        if (!this.mapLoaded) this.showMapFailure(this.dataset.timeoutMessage);
      }, MAP_TIMEOUT_MS);

      this.map.once('load', async () => {
        clearTimeout(this.loadTimer);
        this.mapLoaded = true;
        this.mapLoading = false;
        this.hideMapFailure();
        this.applyMapColors();
        const map = this.map;
        try {
          await this.addNodeLayers();
        } catch {
          if (this.map === map) this.showMapFailure(this.dataset.errorMessage);
          return;
        }
        if (this.map !== map) return;
        if (this.cardsElement?.hasAttribute('hidden')) {
          this.setInitialView();
          this.updateSelectedFeature();
        } else {
          this.selectNode(this.selectedIndex, true);
        }
      });

      this.map.on('error', () => {
        if (!this.mapLoaded) this.showMapFailure(this.dataset.errorMessage);
      });
    } catch {
      this.mapLoading = false;
      this.showMapFailure(this.dataset.unsupportedMessage);
    }
  }

  applyMapColors() {
    if (!this.mapLoaded) return;
    const colors = {
      land: this.dataset.landColor,
      water: this.dataset.waterColor,
      border: this.dataset.borderColor,
      label: this.dataset.labelColor,
    };

    for (const layer of this.map.getStyle().layers ?? []) {
      const name = `${layer.id} ${layer['source-layer'] ?? ''}`.toLowerCase();
      try {
        if (layer.type === 'background') this.map.setPaintProperty(layer.id, 'background-color', colors.land);
        if (layer.type === 'fill' && /water|ocean|lake|river/.test(name)) {
          this.map.setPaintProperty(layer.id, 'fill-color', colors.water);
        } else if (layer.type === 'fill' && /land|landcover|landuse|earth/.test(name)) {
          this.map.setPaintProperty(layer.id, 'fill-color', colors.land);
        }
        if (layer.type === 'line' && /boundary|border|admin/.test(name)) {
          this.map.setPaintProperty(layer.id, 'line-color', colors.border);
        }
        if (layer.type === 'symbol') {
          if (this.dataset.showMapLabels !== 'true' && layer.layout?.['text-field']) {
            this.map.setLayoutProperty(layer.id, 'text-field', '');
          }
          this.map.setPaintProperty(layer.id, 'text-color', colors.label);
        }
      } catch {
        // A style can omit a paint property; the remaining layers still render.
      }
    }
  }

  async addNodeLayers() {
    const map = this.map;
    const icons = await Promise.all([this.dataset.pin, this.dataset.selectedPin].map(async (url) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = 37;
      canvas.height = 51;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, 37, 51);
      return context.getImageData(0, 0, 37, 51);
    }));
    if (this.map !== map) return;
    map.addImage('mission-pin', icons[0]);
    map.addImage('mission-pin-selected', icons[1]);
    const data = {
      type: 'FeatureCollection',
      features: this.nodes.map((node) => ({
        type: 'Feature',
        id: node.id,
        geometry: { type: 'Point', coordinates: [node.longitude, node.latitude] },
        properties: { id: node.id },
      })),
    };

    this.map.addSource(MAP_SOURCE, {
      type: 'geojson',
      data,
      cluster: this.dataset.clusterEnabled === 'true',
      clusterRadius: 50,
      clusterMaxZoom: Math.max(0, Math.floor(this.selectedZoom - 1)),
    });
    this.map.addLayer({
      id: CLUSTER_LAYER,
      type: 'circle',
      source: MAP_SOURCE,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': this.dataset.clusterColor,
        'circle-radius': ['step', ['get', 'point_count'], 22, 10, 28, 30, 34],
      },
    });
    this.map.addLayer({
      id: CLUSTER_COUNT_LAYER,
      type: 'symbol',
      source: MAP_SOURCE,
      filter: ['has', 'point_count'],
      layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 14 },
      paint: { 'text-color': this.dataset.clusterTextColor },
    });
    this.map.addLayer({
      id: POINT_LAYER,
      type: 'symbol',
      source: MAP_SOURCE,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'mission-pin',
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    this.map.on('click', POINT_LAYER, (event) => {
      const id = event.features?.[0]?.properties?.id;
      const index = this.nodes.findIndex((node) => node.id === String(id));
      if (index >= 0) this.selectNode(index, true);
    });
    this.map.on('click', CLUSTER_LAYER, (event) => this.expandCluster(event));
    for (const layer of [POINT_LAYER, CLUSTER_LAYER]) {
      this.map.on('mouseenter', layer, () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', layer, () => { this.map.getCanvas().style.cursor = ''; });
    }
  }

  setInitialView() {
    if (!this.mapLoaded) return;
    if (this.dataset.initialView === 'manual' || this.nodes.length === 1) {
      const node = this.nodes[0];
      const center = this.dataset.initialView === 'manual'
        ? [this.initialLongitude, this.initialLatitude]
        : [node.longitude, node.latitude];
      this.map.jumpTo({ center, zoom: this.initialZoom });
      return;
    }

    const longitudes = this.unwrapLongitudes(this.nodes.map((node) => node.longitude));
    const bounds = new maplibregl.LngLatBounds();
    this.nodes.forEach((node, index) => bounds.extend([longitudes[index], node.latitude]));
    const desktop = this.getBoundingClientRect().width >= 1280;
    const camera = this.map.cameraForBounds(bounds, {
      padding: desktop ? { top: 70, right: 720, bottom: 70, left: 70 } : 50,
      maxZoom: this.maxZoom,
    });
    if (camera) this.map.jumpTo(camera);
  }

  unwrapLongitudes(longitudes) {
    if (longitudes.length < 2) return longitudes;
    const normalized = longitudes.map((value, index) => ({ value: ((value % 360) + 360) % 360, index })).sort((a, b) => a.value - b.value);
    let largestGap = -1;
    let splitIndex = 0;
    normalized.forEach((entry, index) => {
      const next = normalized[(index + 1) % normalized.length];
      const gap = (next.value + (index === normalized.length - 1 ? 360 : 0)) - entry.value;
      if (gap > largestGap) {
        largestGap = gap;
        splitIndex = (index + 1) % normalized.length;
      }
    });
    const result = [];
    for (let offset = 0; offset < normalized.length; offset += 1) {
      const position = (splitIndex + offset) % normalized.length;
      const entry = normalized[position];
      result[entry.index] = entry.value + (position < splitIndex ? 360 : 0);
    }
    return result;
  }

  async expandCluster(event) {
    const feature = event.features?.[0];
    const clusterId = feature?.properties?.cluster_id;
    if (clusterId === undefined) return;
    const source = this.map.getSource(MAP_SOURCE);
    try {
      const expansionZoom = Math.min(await source.getClusterExpansionZoom(clusterId), this.maxZoom);
      this.map.easeTo({ center: feature.geometry.coordinates, zoom: expansionZoom });
    } catch {
      this.showMapFailure(this.dataset.clusterErrorMessage);
    }
  }

  selectNode(index, moveMap = true) {
    if (!this.nodes.length) return;
    this.selectedIndex = (index + this.nodes.length) % this.nodes.length;
    const selected = this.nodes[this.selectedIndex];
    this.cardsElement?.removeAttribute('hidden');
    this.cards.forEach((card) => { card.hidden = card.dataset.nodeId !== selected.id; });
    this.listButtons.forEach((button) => button.setAttribute('aria-current', String(button.dataset.nodeId === selected.id)));
    this.querySelectorAll('[data-city-current]').forEach((counter) => { counter.textContent = String(this.selectedIndex + 1); });
    this.querySelectorAll('[data-city-previous], [data-city-next]').forEach((button) => { button.disabled = this.nodes.length < 2; });
    this.updateSelectedFeature();

    if (moveMap && this.mapLoaded) {
      const desktop = this.getBoundingClientRect().width >= 1280;
      const mapRect = this.mapElement.getBoundingClientRect();
      const cardsRect = this.cardsElement?.getBoundingClientRect();
      const coveredHeight = desktop ? 0 : Math.max(0, mapRect.bottom - Math.max(mapRect.top, cardsRect?.top ?? mapRect.bottom));
      this.map.easeTo({
        center: [selected.longitude, selected.latitude],
        zoom: Math.max(this.map.getZoom(), this.selectedZoom),
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        offset: desktop ? [-325, 0] : [0, -coveredHeight / 2],
      });
    }
  }

  updateSelectedFeature() {
    if (!this.mapLoaded || !this.map.getSource(MAP_SOURCE)) return;
    if (!this.map.getLayer(POINT_LAYER)) return;
    this.selectedFeatureId = this.nodes[this.selectedIndex]?.id ?? null;
    this.map.setLayoutProperty(POINT_LAYER, 'symbol-sort-key', [
      'case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId ?? ''], 1, 0,
    ]);
    this.map.setLayoutProperty(POINT_LAYER, 'icon-image', [
      'case', ['==', ['to-string', ['get', 'id']], this.selectedFeatureId ?? ''],
      'mission-pin-selected', 'mission-pin',
    ]);
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const selectButton = event.target.closest('[data-city-select]');
    if (selectButton) {
      const index = this.nodes.findIndex((node) => node.id === selectButton.dataset.nodeId);
      if (index >= 0) this.selectNode(index, true);
      return;
    }
    if (event.target.closest('[data-city-previous]')) this.selectNode(this.selectedIndex - 1, true);
    if (event.target.closest('[data-city-next]')) this.selectNode(this.selectedIndex + 1, true);
    if (event.target.closest('[data-city-close]')) {
      this.cardsElement?.setAttribute('hidden', '');
      if (this.mapLoaded) this.map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 });
      if (this.listPanel?.open) this.nodes[this.selectedIndex]?.button.focus({ preventScroll: true });
      else this.listToggle?.focus({ preventScroll: true });
    }
    if (event.target.closest('[data-city-list-toggle]')) {
      this.listPanel.open = !this.listPanel.open;
      this.listToggle?.setAttribute('aria-expanded', String(this.listPanel.open));
      if (this.listPanel.open) this.nodes[this.selectedIndex]?.button.focus();
    }
    if (event.target.closest('[data-map-retry]')) this.retryMap();
  };

  retryMap() {
    clearTimeout(this.loadTimer);
    this.map?.remove();
    this.map = null;
    this.mapLoaded = false;
    this.mapLoading = false;
    this.initializeMap();
  }

  showMapFailure(message) {
    this.setAttribute('data-map-failed', '');
    if (this.listPanel) this.listPanel.open = true;
    this.listToggle?.setAttribute('aria-expanded', 'true');
    if (this.statusMessage && message) this.statusMessage.textContent = message;
    this.statusElement?.removeAttribute('hidden');
  }

  hideMapFailure() {
    this.removeAttribute('data-map-failed');
    this.statusElement?.setAttribute('hidden', '');
  }
}

if (!customElements.get('mission-city-nodes')) {
  customElements.define('mission-city-nodes', MissionCityNodes);
}
